import { AxiosResponse } from "axios";
import { intersectionBy, isEmpty, omitBy, pick } from "lodash";
import moment from "moment";
import { NavigateOptions } from "react-router-dom";
import { NotificationsLevel, TimeEnum } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import {
  CloudBulkSearchResponse,
  LocalBulkSearchResponse
} from "../../../services/@types";
import { parseSortString, parseURLParams } from "../../../utils";
import {
  CloudSample,
  GridConfig,
  LocalSample,
  SearchParams,
  Selections,
  Sort
} from "../@types";
import {
  extractFirstSeen,
  getTimerangeFromEnum,
  hasOnlyOneInstanceOfFirstSeen,
  queryIsSimple,
  replaceFirstSeen
} from "../components/utils";
import { SAMPLE_ORIGINS } from "../components/filters/consts";
import {
  CLOUD_LIMIT_MOMENT,
  DATE_FILTER,
  FILTER_KEYWORDS,
  ISO_YEAR_MONTH_DAY_FORMAT_UTC,
  LOCATION
} from "../consts";
import { NavigateProps } from "../services/@types";
import { determineSelectionsFromQP, transformGridData } from "../util";
import {
  DEFAULT_CLOUD_SORT_SELECTION,
  DEFAULT_CLOUD_TIME_SELECTION,
  DEFAULT_LOCAL_SORT,
  DEFAULT_LOCAL_TIME_SELECTIONS,
  DEFAULT_NETWORK_TIME_SELECTION,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_QUERY
} from "./consts";

const getInvalidHashes = (
  localData: LocalBulkSearchResponse,
  localError: AxiosResponse<string>,
  cloudData: CloudBulkSearchResponse,
  cloudError: AxiosResponse<string>
) => {
  if (localError && cloudError) {
    return { invalid_hashes: {} };
  }
  if (cloudError)
    return {
      invalid_hashes: {
        data: localData.invalid_hashes || [],
        count: localData.invalid_hashes.length,
        loading: false
      }
    };
  if (localError) {
    return {
      invalid_hashes: {
        data: cloudData.invalid_hashes || [],
        count: cloudData.invalid_hashes.length,
        loading: false
      }
    };
  }
  const invalidHashes = intersectionBy(
    localData.invalid_hashes,
    cloudData.invalid_hashes,
    "hash"
  );
  return {
    invalid_hashes: {
      data: invalidHashes || [],
      count: invalidHashes.length,
      loading: false
    }
  };
};

const getUnfoundHashes = (
  localData: LocalBulkSearchResponse,
  localError: AxiosResponse<string>,
  cloudData: CloudBulkSearchResponse,
  cloudError: AxiosResponse<string>
) => {
  if (localError && cloudError) {
    return {
      notfound_hashes: {}
    };
  }
  if (cloudError)
    return {
      notfound_hashes: {
        data: localData.unfound_samples || [],
        count: localData.unfound_samples.length,
        loading: false
      }
    };
  if (localError) {
    return {
      notfound_hashes: {
        data: cloudData.unfound_samples || [],
        count: cloudData.unfound_samples.length,
        loading: false
      }
    };
  }
  const unfound_samples = intersectionBy(
    localData.unfound_samples,
    cloudData.unfound_samples,
    "hash"
  );
  return {
    notfound_hashes: {
      data: unfound_samples || [],
      count: unfound_samples.length,
      loading: false
    }
  };
};

const prepareBulkSearchData = (
  localData: LocalBulkSearchResponse,
  localError: AxiosResponse<string>,
  cloudData: CloudBulkSearchResponse,
  cloudError: AxiosResponse<string>
) => ({
  ...getUnfoundHashes(localData, localError, cloudData, cloudError),
  ...getInvalidHashes(localData, localError, cloudData, cloudError),
  local: {
    data: transformGridData(localData?.data || []) as LocalSample[],
    count: localData?.local_sample_count,
    loading: false,
    show: !localError,
    error: localError
  },
  cloud: {
    data: transformGridData(cloudData?.data || []) as CloudSample[],
    count: cloudData?.data?.length,
    loading: false,
    show: !cloudError,
    error: [401, 403].includes(cloudError?.status) ? null : cloudError
  }
});

const getBulkLocation = (
  counts: Record<string, number>,
  location: string,
  gridConfig: GridConfig
) => {
  if (!counts[location]) {
    const newLocation =
      Object.keys(counts).find(key => counts[key]) ?? LOCATION.LOCAL;

    return location !== newLocation &&
      newLocation === LOCATION.CLOUD &&
      !gridConfig.automatically_switch_to_cloud
      ? location
      : newLocation;
  }
  return location;
};

const createQueryParams = (
  params: URLSearchParams,
  isCloudDisabled = false,
  defaultPageSize = DEFAULT_PAGE_SIZE
) => {
  const page = Number(params.get("page") || DEFAULT_PAGE);
  const pagesize = Number(params.get("pagesize") || defaultPageSize);
  const q = params.get("q") || DEFAULT_QUERY;
  const location =
    params.get("location") === LOCATION.CLOUD && isCloudDisabled
      ? LOCATION.LOCAL
      : params.get("location") || LOCATION.LOCAL;
  const sort = parseSortString(
    params.get("sort") || DEFAULT_LOCAL_SORT,
    location
  );
  const isBulk = Boolean(params.get("bulk"));

  const isRecent = params.get("recent") === "true";

  const selectionsParam = params.get("selections") || "";

  const determinedSelections = determineSelectionsFromQP(
    parseURLParams(selectionsParam)
  ) as Selections;

  const queryIsSimpleAndHasFirstSeen =
    queryIsSimple(q) && hasOnlyOneInstanceOfFirstSeen(q);

  const extractedFirstSeen = queryIsSimpleAndHasFirstSeen
    ? extractFirstSeen(q)
    : undefined;

  const firstSeenShouldReset =
    queryIsSimpleAndHasFirstSeen &&
    location === LOCATION.CLOUD &&
    (determinedSelections?.period_type?.value.includes("*") ||
      determinedSelections?.period_type?.label === TimeEnum.ALL ||
      extractedFirstSeen?.includes("*"));

  const replacementTimerange = firstSeenShouldReset
    ? getTimerangeFromEnum(TimeEnum.LAST_MONTH)
    : extractedFirstSeen;

  const modifiedQuery = firstSeenShouldReset
    ? replaceFirstSeen(q, getTimerangeFromEnum(TimeEnum.LAST_MONTH))
    : q;

  const syncedFirstSeen =
    queryIsSimpleAndHasFirstSeen &&
    determinedSelections?.time_type?.value === FILTER_KEYWORDS.FIRST_SEEN
      ? replacementTimerange
      : undefined;

  const shouldShowExtractedFiles =
    location === LOCATION.LOCAL &&
    !q &&
    determinedSelections?.sample_origin?.value
      ?.split(",")
      ?.includes(SAMPLE_ORIGINS.EXTRACTED_FILES);

  const submissionTimeCannotHaveResults =
    location === LOCATION.NETWORK &&
    !!q &&
    determinedSelections?.time_type?.value === FILTER_KEYWORDS.SUBMISSION_TIME;

  const timeTypeShouldSwitchToFirstSeen =
    shouldShowExtractedFiles || submissionTimeCannotHaveResults;

  const defaultTimeSelections = {
    [LOCATION.CLOUD]: DEFAULT_CLOUD_TIME_SELECTION,
    [LOCATION.LOCAL]: DEFAULT_LOCAL_TIME_SELECTIONS,
    [LOCATION.NETWORK]: DEFAULT_NETWORK_TIME_SELECTION
  };

  const selections = {
    ...(defaultTimeSelections[location] ?? {}),
    ...determinedSelections,
    ...(timeTypeShouldSwitchToFirstSeen && {
      time_type: {
        label: FILTER_KEYWORDS.FIRST_SEEN,
        value: FILTER_KEYWORDS.FIRST_SEEN
      }
    }),
    ...(syncedFirstSeen
      ? { period_type: { label: "custom", value: syncedFirstSeen } }
      : {}),
    // switch selection value to first seen when bulk search and cloud location
    ...(isBulk && location === LOCATION.CLOUD
      ? pick(DEFAULT_CLOUD_TIME_SELECTION, ["time_type"])
      : {})
  };

  return omitBy(
    {
      q: modifiedQuery,
      selections,
      page,
      pagesize,
      location,
      sort,
      isRecent,
      isBulk
    },
    value => (typeof value === "object" ? isEmpty(value) : !value)
  );
};

const switchToCloud = (
  navigate: (props: NavigateProps, options?: NavigateOptions) => void,
  params: SearchParams
) => {
  const { CLASSIFICATION, PERIOD_TYPE } = FILTER_KEYWORDS;
  const cloudSelections = pick(params.selections, [
    CLASSIFICATION,
    PERIOD_TYPE
  ]);
  navigate(
    {
      ...params,
      sort:
        params.sort.field === "submission_time"
          ? (DEFAULT_CLOUD_SORT_SELECTION as Sort)
          : params.sort,
      location: LOCATION.CLOUD,
      isRecent: false,
      selections: {
        ...cloudSelections,
        time_type: {
          value: FILTER_KEYWORDS.FIRST_SEEN,
          label: FILTER_KEYWORDS.FIRST_SEEN
        }
      }
    },
    {
      replace: true
    }
  );
};

const expandSearchToPreviousYear = (
  navigate: (props: NavigateProps, options?: NavigateOptions) => void,
  params: SearchParams,
  nextPeriod: {
    start_search_date?: string | null;
    end_search_date?: string | null;
  }
) => {
  const { start_search_date, end_search_date } = nextPeriod;
  navigate(
    {
      ...params,
      isRecent: false,
      selections: {
        ...params.selections,
        period_type: {
          value: `[${moment(start_search_date || "")
            .subtract(11, "months")
            .format(ISO_YEAR_MONTH_DAY_FORMAT_UTC)} TO ${end_search_date}]`,
          label: DATE_FILTER.CUSTOM
        }
      }
    },
    {
      replace: true
    }
  );
};

const expandSearchPeriodBeyondAYear = (
  navigate: (props: NavigateProps, options?: NavigateOptions) => void,
  params: SearchParams,
  nextPeriod: {
    start_search_date?: string | null;
    end_search_date?: string | null;
  }
) => {
  const { start_search_date } = nextPeriod;

  const nextYear = moment(start_search_date).subtract(1, "years");

  const nextEndSearchDate = nextYear.isBefore(CLOUD_LIMIT_MOMENT)
    ? CLOUD_LIMIT_MOMENT.format(ISO_YEAR_MONTH_DAY_FORMAT_UTC)
    : nextYear.format(ISO_YEAR_MONTH_DAY_FORMAT_UTC);

  if (moment(start_search_date).isSame(CLOUD_LIMIT_MOMENT)) return;

  navigate(
    {
      ...params,
      isRecent: false,
      selections: {
        ...params.selections,
        period_type: {
          value: `[${nextEndSearchDate} TO ${start_search_date}]`,
          label: DATE_FILTER.CUSTOM
        }
      }
    },
    {
      replace: true
    }
  );
};

const getSearchError = (
  queryError: ErrorResponse<MessageErrorResponse> | null,
  queryData: unknown[] | undefined,
  validationError: unknown,
  validationErrMsg?: string | null
): ErrorResponse<MessageErrorResponse> | string | null => {
  if (queryError) return queryError;
  if (validationError)
    return validationError as ErrorResponse<MessageErrorResponse>;
  if (queryData?.length) return null;
  return validationErrMsg ?? null;
};

const isQueryEncoded = (value: string) => {
  try {
    const decoded = decodeURIComponent(value);
    return value !== decoded;
  } catch (error) {
    return false;
  }
};

const getCloudSelections = (selections: Selections) => {
  const { CLASSIFICATION, CLOUD_SEARCH_TYPE } = FILTER_KEYWORDS;
  return pick(selections, [CLASSIFICATION, CLOUD_SEARCH_TYPE]);
};

const showCancelRequestNotification = () => {
  show({
    message: "Requests have been cancelled.",
    level: NotificationsLevel.INFO
  });
};

export {
  createQueryParams,
  expandSearchPeriodBeyondAYear,
  expandSearchToPreviousYear,
  getBulkLocation,
  getCloudSelections,
  getSearchError,
  isQueryEncoded,
  prepareBulkSearchData,
  switchToCloud,
  showCancelRequestNotification
};
